import 'isomorphic-unfetch';
import { useMemo } from 'react';
import {
  ApolloClient,
  ApolloLink,
  ApolloProvider,
  createHttpLink,
  InMemoryCache,
  ServerError,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import { AuthInstance } from '@guildeducationinc/guild-auth';
import { datadogRum } from '@datadog/browser-rum';

export enum Server {
  CATALOG = 'catalog',
  EMPLOYMENT_SERVICE = 'employmentService',
  GLOBAL_APP_SYNC = 'globalAppSync',
  // currently, UPSv2 does not accept user access tokens via global app sync
  // TO-DO: BU to update when UPSv2 allows for user access tokens
  // https://guild-education.atlassian.net/browse/BU-1614
  GLOBAL_APP_SYNC_ID_TOKEN = 'globalAppSyncIdToken',
  MEMBER_TRAIT_SERVICE = 'memberTraitService',
  SELF_REPORTED_DATA = 'selfReportedData',
  UPS = 'ups',
  UPSV2 = 'upsV2',
  LEARNING_LIFECYCLE_SERVICE = 'learningLifecycleService',
  TUITION_REIMBURSEMENT = 'tuitionReimbursement',
  GATEWAY = 'gateway',
}

interface AuthLinkOptions {
  bearer?: boolean;
}

const GqlProvider: React.FC = ({ children }) => {
  const accessAuthLink = (options?: AuthLinkOptions) => {
    return setContext(async (_, { headers }) => {
      const token = await AuthInstance.getAccessToken();

      return {
        headers: {
          ...headers,
          authorization: `${options?.bearer ? 'Bearer ' : ''}${token}`,
        },
      };
    });
  };

  const idAuthLink = (options?: AuthLinkOptions) => {
    return setContext(async (_, { headers }) => {
      const token = await AuthInstance.getIdToken();

      return {
        headers: {
          ...headers,
          Authorization: `${options?.bearer ? 'Bearer ' : ''}${token}`,
        },
      };
    });
  };

  const retryLink = new RetryLink({
    delay: {
      initial: 300,
      jitter: true,
      max: 5000,
    },
    attempts: {
      max: 1,
    },
  });

  const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
    if (graphQLErrors) {
      graphQLErrors.forEach(({ message, locations, path }) => {
        datadogRum.addError(graphQLErrors, {
          message: `[GraphQL error]: ${message}`,
          Location: `${JSON.stringify(locations)}`,
          Path: `${path}`,
        });
      });
    }
    if (networkError) {
      const networkErrorTitle = `${operation.operationName} [Network error]`;
      if ((networkError as ServerError).statusCode === 401) {
        // eslint-disable-next-line no-console
        console?.error(networkErrorTitle, networkError);
      } else {
        datadogRum.addError(new Error(networkErrorTitle), {
          message: networkError,
        });
      }
    }
  });

  const appSyncAuthLink = accessAuthLink();

  const appSyncAuthLinkIdToken = idAuthLink();

  const catalogAuthLink = idAuthLink({ bearer: true });

  const employmentServiceAuthLink = accessAuthLink();

  const memberTraitServiceAuthLink = idAuthLink();

  const selfReportedDataAuthLink = idAuthLink({ bearer: true });

  const upsAuthLink = idAuthLink();

  const upsV2AuthLink = idAuthLink();

  const learningLifecycleServiceAuthLink = accessAuthLink();

  const tuitionReimbursementAuthLink = idAuthLink({ bearer: true });

  const appSyncHttpLink = createHttpLink({
    fetch,
    uri: `${process.env.REACT_APP_APP_SYNC_ENDPOINT}/graphql`,
  });

  const catalogHttpLink = createHttpLink({
    fetch,
    uri: `${process.env.REACT_APP_CATALOG_SERVICE_URL}/graphql`,
  });

  const memberTraitServiceHttpLink = createHttpLink({
    fetch,
    uri: `${process.env.REACT_APP_MEMBER_TRAIT_SERVICE_URL}/graphql`,
  });

  const selfReportedDataHttpLink = createHttpLink({
    fetch,
    uri: `${process.env.REACT_APP_SELF_REPORTED_DATA_SERVICE_URL}/graphql`,
  });

  const upsHttpLink = createHttpLink({
    fetch,
    uri: `${process.env.REACT_APP_UPS_URL}/graphql`,
  });

  const learningLifecycleServiceHttpLink = createHttpLink({
    fetch,
    uri: `${process.env.REACT_APP_LEARNING_LIFECYCLE_SERVICE_URL}/graphql`,
  });

  const tuitionReimbursementHttpLink = createHttpLink({
    fetch,
    uri: `${process.env.REACT_APP_TUITION_SERVICE_URL}/graphql`,
  });

  const gatewayHttpLink = createHttpLink({
    fetch,
    uri: `${process.env.REACT_APP_GATEWAY_URL}/graphql`,
  });

  const setApolloRequestLink = () => {
    return ApolloLink.split(
      (operation) => operation.getContext().server === 'catalog',
      catalogHttpLink,
      ApolloLink.split(
        (operation) => operation.getContext().server === 'employmentService',
        appSyncHttpLink,
        ApolloLink.split(
          (operation) => operation.getContext().server === 'memberTraitService',
          memberTraitServiceHttpLink,
          ApolloLink.split(
            (operation) => operation.getContext().server === 'selfReportedData',
            selfReportedDataHttpLink,
            ApolloLink.split(
              (operation) => operation.getContext().server === 'ups',
              upsHttpLink,
              ApolloLink.split(
                (operation) => operation.getContext().server === 'learningLifecycleService',
                learningLifecycleServiceHttpLink,
                ApolloLink.split(
                  (operation) => operation.getContext().server === 'tuitionReimbursement',
                  tuitionReimbursementHttpLink,
                  ApolloLink.split(
                    (operation) => operation.getContext().server === 'gateway',
                    gatewayHttpLink,
                    appSyncHttpLink
                  )
                )
              )
            )
          )
        )
      )
    );
  };

  const setApolloAuthLink = () => {
    return ApolloLink.split(
      (operation) => operation.getContext().server === 'catalog',
      catalogAuthLink,
      ApolloLink.split(
        (operation) => operation.getContext().server === 'employmentService',
        employmentServiceAuthLink,
        ApolloLink.split(
          (operation) => operation.getContext().server === 'memberTraitService',
          memberTraitServiceAuthLink,
          ApolloLink.split(
            (operation) => operation.getContext().server === 'selfReportedData',
            selfReportedDataAuthLink,
            ApolloLink.split(
              (operation) => operation.getContext().server === 'ups',
              upsAuthLink,
              ApolloLink.split(
                (operation) => operation.getContext().server === 'upsV2',
                upsV2AuthLink,
                ApolloLink.split(
                  (operation) => operation.getContext().server === 'learningLifecycleService',
                  learningLifecycleServiceAuthLink,
                  ApolloLink.split(
                    (operation) => operation.getContext().server === 'tuitionReimbursement',
                    tuitionReimbursementAuthLink,
                    ApolloLink.split(
                      (operation) => operation.getContext().server === 'globalAppSyncIdToken',
                      appSyncAuthLinkIdToken,
                      appSyncAuthLink
                    )
                  )
                )
              )
            )
          )
        )
      )
    );
  };

  const client = useMemo(() => {
    return new ApolloClient({
      cache: new InMemoryCache({
        addTypename: false,
        typePolicies: {
          Query: {
            fields: {
              getTraits: { merge: true },
            },
          },
        },
      }),
      connectToDevTools: process.env.NODE_ENV === 'development',
      link: ApolloLink.from([setApolloAuthLink(), retryLink, errorLink, setApolloRequestLink()]),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
};

export default GqlProvider;
